import { useState, useEffect } from "react";
import { Edit3, Save, X } from "lucide-react";
import { BASE_URL } from "../../../../Api/Config";
export const PersonalInfoSection = ({ userData, onUpdateUser, isEditing, setIsEditing, loading }) => {
  const [formData, setFormData] = useState({
    name: userData.name || '',
    email: userData.email || '',
    phone: userData.phone || '',
  });
  const [errors, setErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  // Update form data when userData changes
  useEffect(() => {
    setFormData({
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
    });
  }, [userData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email address";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsUpdating(true);
    try {
      // API call to update profile
      const token = localStorage.getItem('token');
      const response = await fetch(`${BASE_URL}/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          // Note: Email might not be editable based on your API design
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      onUpdateUser(updatedUser.user || updatedUser);
      setIsEditing(false);
      alert("Profile updated! Your personal information has been saved.");
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(`Error updating profile: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
    });
    setErrors({});
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="rounded-2xl shadow bg-white p-6">
        <div className="space-y-4">
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl shadow bg-white p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Personal Information</h2>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 border px-3 py-1.5 rounded-md hover:bg-gray-50 text-sm font-medium"
          >
            <Edit3 size={16} /> Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              disabled={isUpdating}
              className="flex items-center gap-2 border px-3 py-1.5 rounded-md hover:bg-gray-50 text-sm font-medium disabled:opacity-50"
            >
              <X size={16} /> Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isUpdating}
              className="flex items-center gap-2 bg-redbg text-white px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-50"
            >
              <Save size={16} /> {isUpdating ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-6">
        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">
            Full Name
          </label>
          {isEditing ? (
            <>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full border px-3 py-2 rounded ${errors.name ? "border-red-500" : "border-gray-300"}`}
                disabled={isUpdating}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </>
          ) : (
            <p className="text-gray-800 font-medium">{userData.name}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">
            Email
          </label>
          {isEditing ? (
            <>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full border px-3 py-2 rounded bg-gray-50 cursor-not-allowed ${errors.email ? "border-red-500" : "border-gray-300"}`}
                disabled={true}
                title="Email cannot be changed"
              />
              <p className="text-gray-500 text-xs mt-1">Email cannot be modified</p>
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </>
          ) : (
            <p className="text-gray-800 font-medium">{userData.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-gray-600 mb-1">
            Phone
          </label>
          {isEditing ? (
            <>
              <input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full border px-3 py-2 rounded ${errors.phone ? "border-red-500" : "border-gray-300"}`}
                disabled={isUpdating}
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </>
          ) : (
            <p className="text-gray-800 font-medium">{userData.phone}</p>
          )}
        </div>
      </div>
    </div>
  );
};